import { FC } from 'react';
import clsx from 'clsx';
import { FormInstance } from 'antd';
import { FormListFieldData } from 'antd/es/form/FormList';
import CustomInput from '../../../../../../Common/CustomInput';
import styles from '../../index.module.less';
import { EnumReportTypes, ILoanColumn, IReportRow } from '../../../../../../../types/reports';
import { IReport, IReportLoan } from '../../../../../../../hooks/reports';
import { getSuffixDetails } from '../../../../constants';
import { useContextReports } from '../../../../../../../context/reports';
import { formatAmount } from '../../../../../../../utils/formatAmount';

type Props = {
  row: IReportRow;
  field: FormListFieldData;
  loan: ILoanColumn;
  loans: ILoanColumn[];
  form: FormInstance;
  isFieldDisabled?: boolean;
  handleLoansUpdate?: (event?: unknown, withLoading?: boolean) => void;
  inputsCount: number;
  disabled: boolean;
  reportType?: EnumReportTypes;
  itemKey: number;
  report?: IReport;
  item: any;
  filterLoansWithSegment: (loans: ILoanColumn[]) => ILoanColumn[];
  isReadOnly?: boolean;
  reportLoans?: IReportLoan[];
  enableComparisonDisplay?: boolean;
};

const Field: FC<Props> = ({
  row,
  field,
  loan,
  loans,
  form,
  isFieldDisabled,
  handleLoansUpdate,
  inputsCount,
  disabled,
  reportType,
  itemKey,
  report,
  item,
  filterLoansWithSegment,
  isReadOnly = false,
  reportLoans,
  enableComparisonDisplay = false
}) => {
  const rentActive = form.getFieldValue(['tableView', 'rent', 'active']);
  const rent = form.getFieldValue(['loans', itemKey, 'externalLoan', 'rent'])

  const {
    reportLoanClosingCosts,
    reportClosingCosts,
    handleReportSettingsUpdate
  } = useContextReports();

  const { suffix, suffixContent } = getSuffixDetails(row, loan, form);

  const valueDecimalPlaces = 2;
  const isExternalLoans = row?.isExternalLoans;

  // @ts-ignore loan?.type
  const isLoanTypeBorrower = loan?.type === 'borrower';

  const isDisabled = row.disabled?.(loan) || disabled
    || isFieldDisabled || (row.name === 'closingCosts' && !report?.csActive)
    || (row.name === 'downPayment' && loan?.externalLoan?.removeDownPayment)

  const tabIndex = (row?.order ?? 0) + itemKey * inputsCount + 1

  const options = row.options ? row.options() : undefined

  const handleChange = (value: string) => {
    row.onChange?.(form, itemKey, value, undefined, loan, reportLoanClosingCosts, reportClosingCosts);
  };

  const handleBlur = (value: string | number | readonly string[] | undefined): void => {
    // @ts-ignore for row.name
    const oldValue = isExternalLoans ? reportLoans?.[field.name]?.externalLoan?.[row.name]
      : reportLoans?.[field.name]?.[row.name];

    // const formattedOldValue = row?.prefix === '$' && oldValue ? formatAmount(parseFloat(oldValue), {
    //   max: valueDecimalPlaces
    // }) : oldValue

    if (value !== oldValue && handleLoansUpdate) {
      handleLoansUpdate({}, true);
      if (row.name === 'loanType') {
        handleReportSettingsUpdate?.('tableView');
      }
    }
  };

  const identifier = isExternalLoans ? ['loans', field.name, 'externalLoan', row.name] :
    ['loans', field.name, row.name, 'value']

  const name = isExternalLoans ? [field.name, 'externalLoan', row.name] : [field.name, row.name, 'value'];

  if (rentActive && rent && row.name !== 'total' && row.name !== 'rent' && row.name !== 'loanType') {
    return (
      <p style={{ margin: 0 }}>{row.prefix}0{suffix}</p>
    )
  }

  if (
    isLoanTypeBorrower &&
    row.name === 'closingCosts') {
    return (
      <p style={{ margin: 0 }}>-</p>
    )
  }

  return (
    <CustomInput
      withEclipses
      formItemProps={{
        ...field,
        className: 'w-100 table-form-item',
        name,
      }}
      suffixContent={suffixContent}
      tabIndex={tabIndex}
      value={row.prepareData?.(loan, loans, form, itemKey)}
      identifier={identifier}
      prefix={row.prefix}
      suffix={suffix}
      disabled={isDisabled}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={row.placeholder || '0'}
      inputClassName={clsx({ [styles.bolded]: row.bolded}, "custom-input-focused-border")}
      options={options}
      onSelect={(value) => row.onSelect?.(form, itemKey, value)}
      activeOption={item?.value}
      showArrow={!!row.options}
      additionalText={row.additionalText?.(loan, filterLoansWithSegment(loans), reportType, enableComparisonDisplay)}
      rowStyles={row.subField ? { minHeight: 0 } : undefined}
      popoverContent={row.getPopoverContent?.(field, inputsCount, form, handleLoansUpdate)}
      isReadOnly={isReadOnly}
      valueDecimalPlaces={valueDecimalPlaces}
      inputType={row?.inputType}
      informationText={row?.informationText?.(loan, form)}
    />
  )
}

export default Field
